import { useCallback, useEffect, useState } from "react"
import AuthLayout from "./layout"
import { useNavigate, useLocation } from "react-router-dom"
import { DASHBOARD_PAGE_ROUTE, LOGIN_PAGE_ROUTE, NEW_DASHBOARD_PAGE_ROUTE, PROFILE_PAGE_ROUTE } from "@pma/utils/page-routes"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { ALL_ORG_USERS_ENDPOINT, ME_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useAuth } from "@stores/auth-store"
import useTranslator from "@hooks/use-translator"
import { DictType } from "@pma/types"
import { toast } from "@pma/hooks/use-toast"

const CompletePage = () => {
  const [orgId, setOrgId] = useState("")
  const navigate = useNavigate()
  const location = useLocation()
  const { translate } = useTranslator()
  const { signIn } = useAuth()

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const timeNow = new Date()
  const timezone = timeNow.getTimezoneOffset() * -1

  const getMeDetails = async () => {
    if (!accessToken || accessToken === "undefined") {
      throw new Error("No access token provided")
    }
    const response = await axios.get(SERVER_URL + ME_ENDPOINT, {
      params: {
        time_offset: timezone,
      },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    setOrgId(response?.data?.last_organization_id)

    return response.data
  }

  const getUsers = async (orgId: string) => {
    if (!accessToken || accessToken === "undefined") {
      throw new Error("No access token provided")
    }
    const response = await axios.get(
      SERVER_URL + ALL_ORG_USERS_ENDPOINT + `?organization_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const {
    data: orgUsers,
    isLoading,
    error,
    isError,
    isFetched: orgUserFetched,
  } = useQuery({
    queryKey: [`org-users`, orgId],
    queryFn: () => getUsers(orgId),
    enabled: !!orgId,
  })

  const { data: userDetails, isFetched } = useQuery({
    queryKey: ["me-details", accessToken],
    queryFn: getMeDetails,
    enabled: !!accessToken,
  })

  const pushOrgToLocalStorage = useCallback(() => {
    // Save organization users to state
    signIn({ organizationUsers: orgUsers?.items })

    signIn({ ...userDetails })

    if (isFetched && orgUserFetched) {
      const from = (location.state as any)?.from?.pathname
      setTimeout(() => {
        const fromPath = location.state?.from || ""
        const isV1 = fromPath.includes("/v1/")
        navigate(isV1 ? NEW_DASHBOARD_PAGE_ROUTE : DASHBOARD_PAGE_ROUTE)
      }, 1000)
    }
  }, [isFetched, orgUserFetched, orgId])

  useEffect(() => {
    if (!accessToken || accessToken === "undefined") {
      toast({
        title: translate("Error"),
        description: translate("No valid session found"),
        variant: "error",
      })
      navigate(LOGIN_PAGE_ROUTE)
      return
    }
    const getUsersError: DictType | null = error

    if (isError || error) {
      if (getUsersError?.message === "No access token provided") {
        toast({
          title: translate("Error"),
          description: translate("No valid session found"),
          variant: "error",
        })
        navigate(LOGIN_PAGE_ROUTE)
        return
      }
      if (getUsersError?.response?.data?.message === "The password is expired.") {
        toast({
          title: translate("Info"),
          description: translate("Your password has expired"),
          variant: "info",
        })

        return navigate(PROFILE_PAGE_ROUTE + `?password_expired=true`)
      }
    } else if (accessToken && isFetched && orgUserFetched && !isError) {
      pushOrgToLocalStorage()
    }
  }, [pushOrgToLocalStorage, accessToken, isError])
  return (
    <AuthLayout>
      <>
        <div
          className='h-full w-[90vw] rounded-md md:w-[600px] lg:w-[800px]'
          style={{
            background: "linear-gradient(blue 5.01%, rgb(232, 203, 203, 0%)",
            borderRadius: "15px 15px 0px 0px",
          }}
        >
          <div className='mx-auto flex w-full flex-1 flex-col justify-center space-y-6 py-12'>
            <div className='dark:text-white-fade flex w-full flex-col items-center space-y-2 text-center text-white'>
              <h1 className='mb-4 text-2xl font-semibold capitalize md:text-4xl'>
                {translate("Welcome")} {userDetails?.name}
              </h1>
              <div className=''>
                <img
                  src={userDetails?.user_picture?.url || `${process.env.PUBLIC_URL}/group-232.svg`}
                  alt='Brand logo'
                  className='h-[250px] w-[250px] rounded-full'
                />
              </div>

              <p className='animate-pulse pt-3 text-base text-muted'>
                {translate("A moment please")}...
              </p>
            </div>
          </div>
        </div>
      </>
    </AuthLayout>
  )
}

export default CompletePage
